import React from "react";
import { motion } from "framer-motion";
import {
  Scale, ShieldAlert, RotateCcw, CircleCheck, CircleHelp, CircleDot,
  Lightbulb, TrendingUp, TrendingDown, AlertTriangle, Sparkles,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ReportButton } from "@/components/ReportSheet";

const LEVELS = { Low: 34, Medium: 67, High: 100, low: 34, medium: 67, high: 100 };
const levelPct = (l) => LEVELS[l] ?? 50;

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.4, delay: Math.min(i * 0.04, 0.2) },
});

function Section({ label, children, className = "" }) {
  return (
    <div className={`mt-8 ${className}`}>
      {label && <p className="decide-label mb-3">{label}</p>}
      {children}
    </div>
  );
}

function Bar({ pct, tone = "ink" }) {
  const bg = tone === "forest" ? "#2C4C3B" : "#171717";
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-decide-line">
      <motion.div className="h-full rounded-full" style={{ backgroundColor: bg }} initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }} transition={{ duration: 0.7, ease: "easeOut" }} />
    </div>
  );
}

const CONFIDENCE_STYLE = { High: "bg-decide-forest text-white", Moderate: "bg-decide-warn text-white", Low: "bg-decide-mute text-white" };

export default function Result({ analysis, options, decisionId, title }) {
  const { t } = useLang();
  const a = analysis || {};
  const opts = a.options?.length ? a.options : options || [];
  const optByKey = Object.fromEntries(opts.map((o) => [o.key, o]));
  const rec = a.recommendation || {};
  const recOpt = optByKey[rec.option_key];
  const revScore = a.reversibility?.score ?? 50;
  const lvlLabel = (lvl) => (lvl ? t(`level.${lvl}`) : "");
  const confLabel = rec.confidence ? t(`confidence.${rec.confidence}`) : "";

  return (
    <div className="pt-1">
      {a.high_stakes_note ? (
        <motion.div {...fade()} className="mb-6 rounded-2xl border border-decide-warn/40 bg-decide-warnSoft p-4" data-testid="high-stakes-note">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-decide-warn" />
            <p className="text-[13px] leading-relaxed text-decide-ink">{a.high_stakes_note}</p>
          </div>
        </motion.div>
      ) : null}

      <motion.div {...fade()}>
        <p className="decide-label">{t("result.label")}</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-light tracking-tight text-decide-ink">{t("result.title")}</h1>
      </motion.div>

      <Section label={t("result.options")}>
        <div className="grid grid-cols-1 gap-3">
          {opts.map((o, i) => (
            <motion.div key={o.key} {...fade(i)} className="decide-card !p-4 flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-decide-panel text-white text-sm font-bold">{o.key}</span>
              <div>
                <p className="font-semibold text-decide-ink">{o.label}</p>
                {o.description && <p className="text-[13px] text-decide-slate mt-0.5">{o.description}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {a.dimensions?.length > 0 && (
        <Section label={t("result.compare")}>
          <div className="decide-card space-y-5">
            {a.dimensions.map((d, i) => (
              <motion.div key={i} {...fade(i)} data-testid="comparison-dimension">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-decide-ink text-[15px]">{d.label}</p>
                </div>
                {d.note && <p className="text-[12.5px] text-decide-slate mt-0.5">{d.note}</p>}
                <div className="mt-3 space-y-2.5">
                  {opts.map((o) => {
                    const lvl = o.key === "A" ? d.a_level : o.key === "B" ? d.b_level : d[`${o.key.toLowerCase()}_level`];
                    return (
                      <div key={o.key} className="flex items-center gap-3">
                        <span className="w-6 text-xs font-bold text-decide-mute">{o.key}</span>
                        <div className="flex-1"><Bar pct={levelPct(lvl)} tone={o.key === rec.option_key ? "forest" : "ink"} /></div>
                        <span className="w-16 text-right text-[11px] font-semibold uppercase tracking-wide text-decide-slate">{lvlLabel(lvl)}</span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      <Section label={t("result.whatWeKnow")}>
        <div className="space-y-3">
          <FactList icon={CircleCheck} tone="#2C4C3B" title={t("result.facts")} items={a.facts} testid="facts-list" />
          <FactList icon={CircleDot} tone="#B87C4C" title={t("result.assumptions")} items={a.assumptions} testid="assumptions-list" />
          <FactList icon={CircleHelp} tone="#737373" title={t("result.uncertainties")} items={a.uncertainties} testid="uncertainties-list" />
        </div>
      </Section>

      {a.trade_off && (
        <Section label={t("result.tradeoff")}>
          <motion.div {...fade()} className="rounded-2xl border border-decide-line bg-decide-mist p-6" data-testid="tradeoff-card">
            <Scale size={22} className="text-decide-forest" />
            <p className="mt-3 text-xl font-medium leading-snug tracking-tight text-decide-ink">{a.trade_off.headline}</p>
            {a.trade_off.detail && <p className="mt-2 text-[15px] leading-relaxed text-decide-slate">{a.trade_off.detail}</p>}
          </motion.div>
        </Section>
      )}

      {a.challenges?.length > 0 && (
        <Section label={t("result.challenge")}>
          <div className="space-y-3">
            {a.challenges.map((c, i) => (
              <motion.div key={i} {...fade(i)} className="decide-card !p-4 flex items-start gap-3" data-testid="challenge-item">
                <Lightbulb size={18} className="mt-0.5 shrink-0 text-decide-warn" />
                <p className="text-[14.5px] leading-relaxed text-decide-ink">{c}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {a.scenarios && (
        <Section label={t("result.scenarios")}>
          <div className="grid grid-cols-1 gap-3">
            {opts.map((o, i) => {
              const s = a.scenarios[o.key];
              if (!s) return null;
              return (
                <motion.div key={o.key} {...fade(i)} className="decide-card" data-testid="scenario-card">
                  <p className="font-semibold text-decide-ink">{t("result.ifYouChoose", { k: o.key, label: o.label })}</p>
                  <div className="mt-3 space-y-2.5">
                    <div className="flex items-start gap-2.5">
                      <TrendingUp size={16} className="mt-0.5 shrink-0 text-decide-forest" />
                      <p className="text-[14px] text-decide-slate"><span className="font-medium text-decide-ink">{t("result.best")} · </span>{s.best}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <TrendingDown size={16} className="mt-0.5 shrink-0 text-decide-danger" />
                      <p className="text-[14px] text-decide-slate"><span className="font-medium text-decide-ink">{t("result.worst")} · </span>{s.worst}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <p className="mt-4 text-center text-[15px] font-medium text-decide-ink">{t("result.whichRisk")}</p>
        </Section>
      )}

      {a.reversibility && (
        <Section label={t("result.reversibility")}>
          <motion.div {...fade()} className="decide-card" data-testid="reversibility-card">
            <div className="flex items-center gap-2">
              <RotateCcw size={18} className="text-decide-forest" />
              <p className="font-semibold text-decide-ink">{a.reversibility.label}</p>
            </div>
            <div className="relative mt-5 h-2 rounded-full bg-gradient-to-r from-decide-forestLight to-decide-mist">
              <motion.div className="absolute -top-1 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-white bg-decide-panel shadow" initial={{ left: 0 }} whileInView={{ left: `${revScore}%` }} viewport={{ once: true }} transition={{ duration: 0.6 }} />
            </div>
            <div className="mt-2 flex justify-between text-[12px] text-decide-slate">
              <span>{t("result.easyReverse")}</span><span>{t("result.hardReverse")}</span>
            </div>
            {a.reversibility.text && <p className="mt-3 text-[14px] leading-relaxed text-decide-slate">{a.reversibility.text}</p>}
          </motion.div>
        </Section>
      )}

      <Section label={t("result.yourDecision")} className="mt-10">
        <motion.div {...fade()} className="rounded-3xl bg-decide-panel p-6 text-white" data-testid="recommendation-card">
          <div className="flex items-center gap-2 text-decide-panelaccent">
            <Sparkles size={15} />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{t("result.recommendation")}</span>
          </div>
          <p className="mt-3 text-[28px] font-semibold leading-tight tracking-tight" data-testid="recommendation-label">
            {rec.label || (recOpt ? recOpt.label : "—")}
          </p>
          {rec.confidence && (
            <span className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${CONFIDENCE_STYLE[rec.confidence] || "bg-decide-surface/20"}`} data-testid="confidence-badge">
              {t("result.confidence", { x: confLabel })}
            </span>
          )}

          {a.why?.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">{t("result.why")}</p>
              <ol className="mt-3 space-y-3">
                {a.why.map((w, i) => (
                  <li key={i} className="flex gap-3" data-testid="reason-item">
                    <span className="font-mono text-sm text-decide-panelaccent">{i + 1}</span>
                    <span className="text-[15px] leading-relaxed text-white/90">{w}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </motion.div>

        {a.biggest_risk && (
          <motion.div {...fade(1)} className="mt-3 rounded-2xl border border-decide-danger/30 bg-decide-dangerSoft p-5" data-testid="biggest-risk">
            <div className="flex items-center gap-2 text-decide-danger">
              <ShieldAlert size={17} />
              <span className="text-[11px] font-bold uppercase tracking-[0.16em]">{t("result.biggestRisk")}</span>
            </div>
            <p className="mt-2 text-[15px] leading-relaxed text-decide-ink">{a.biggest_risk}</p>
          </motion.div>
        )}

        {a.what_would_change && (
          <motion.div {...fade(2)} className="mt-3 rounded-2xl border border-decide-line bg-decide-surface p-5" data-testid="what-would-change">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-decide-mute">{t("result.whatWouldChange")}</p>
            <p className="mt-2 text-[15px] leading-relaxed text-decide-ink">{a.what_would_change}</p>
          </motion.div>
        )}
      </Section>

      <ReportButton decisionId={decisionId} context={{ title: title || "", recommendation: rec.label || "", confidence: rec.confidence || "" }} />
    </div>
  );
}

function FactList({ icon: Icon, tone, title, items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="decide-card !p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color: tone }} />
        <p className="text-[13px] font-semibold text-decide-ink">{title}</p>
      </div>
      <ul className="mt-2.5 space-y-1.5">
        {items.map((it, i) => (<li key={i} className="text-[14px] leading-relaxed text-decide-slate pl-1">— {it}</li>))}
      </ul>
    </div>
  );
}
